import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'
import { Icon } from '../../icon'

// Tell flatpickr to use shorter weekday initials
const weekdayInitials = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
flatpickr.l10ns.default.weekdays.shorthand = weekdayInitials

export default class DatepickerController extends Controller<HTMLElement> {
  static classes = ['readonly']
  static values = {
    config: Object,
  }

  initialize() {
    this.defaults = {
      appendTo: this.element.closest('.new-drawer') || document.body,
      dateFormat: 'Y-m-d',
      nextArrow: new Icon('icon-chevron-right', { title: 'Next Month' }).asString(),
      prevArrow: new Icon('icon-chevron-left', { title: 'Previous Month' }).asString(),
    }
    this.#parseConfig()
  }

  connect() {
    this.options = {
      ...this.defaults,
      onValueUpdate: this.#handleValueUpdate.bind(this),
      ...this.config,
    }

    this.options.defaultDate = this.#getDefaultDate()

    /* NOTE: If we ever want to make a readonly datepicker writeable
     * without reconnecting the controller, we'll need to do something
     * more complicated here.
     */
    if (!this.element.readOnly) {
      this.element.datepicker = flatpickr(this.element, this.options)

      this.dispatch('init', { detail: { $elem: this.element } })
      this.element.classList.remove(...this.readonlyClasses)
    }

    window.addEventListener('page-content:scroll', this.#handleScroll)
  }

  disconnect() {
    window.removeEventListener('page-content:scroll', this.#handleScroll)
    this.element.classList.add(...this.readonlyClasses)
    this.element.readOnly = this.readOnly
  }

  #getDefaultDate() {
    let date = null

    if ('mode' in this.options && this.options.mode === 'range') {
      const arr = this.element.value.split(' to ')
      date = arr.length === 1 ? arr[0] : arr
    } else {
      date = this.element.value || null
    }

    return date
  }

  #handleScroll = () => {
    this.datepicker?.close()
  }

  #handleValueUpdate(selectedDates, dateStr, instance) {
    this.dispatch('update', { detail: { selectedDates, dateStr, instance } })
  }

  #parseConfig() {
    let config = structuredClone(this.configValue)

    // Some default value handling
    if ('minDate' in config) {
      switch (config.minDate) {
        case 'today':
          config.minDate = new Date()
          break
      }
    }

    // These config values expect a DOM element, but we can't pass those into
    // the config data attribute. Instead, we'll look for CSS selectors in these
    // values and then traverse up the DOM tree from this.element until we
    // find the element we're looking for.
    const elementValues = ['appendTo', 'positionElement']

    elementValues.forEach(key => {
      if (key in config) config[key] = this.element.closest(config[key])
    })

    // These config values expect an integer, but they are passed as strings
    // through data attributes. We'll convert them to integers here.
    const integerValues = ['defaultHour', 'defaultMinute', 'hourIncrement', 'minuteIncrement', 'showMonths']

    integerValues.forEach(key => {
      if (key in config) config[key] = parseInt(config[key], 10)
    })

    this.config = config
  }
}
